import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { jestPolska } from '../kraj'
import { getKitPreferences, getProfile, getQuoteSettings, getZespol, listKlienci } from '../api/account'
import { profilFirmy } from '../api/rynek'
import { klienciWlasciwi } from '../lib/pulpit/pulpit'
import { terminyKlienta } from '../lib/klienci/kalendarz'
import {
  krokPo, krokPoKluczu, maWlasneTerminy, podpisCennika, preferencjeUstawione, stanSamouczka,
} from '../lib/samouczek/kroki'
import { PUSTA_PAMIEC, czytajPamiec, zapiszPamiec } from '../lib/samouczek/pamiec'

/**
 * Samouczek „Pierwsze kroki” (2026-10-13): stan i akcje dla okna powitania, panelu z krokami i podświetlenia
 * miejsca w serwisie (`components/samouczek/`). Kroki i ich stan: `lib/samouczek/kroki.js`.
 *
 * Kto go dostaje: konto firmowe oraz konto bez wybranego typu i bez firmy (firma, która przy rejestracji nie
 * zaznaczyła „Firma”; pytamy ją, czy prowadzi firmę). Samo okno powitania pokazuje się tylko kontom założonym
 * w ostatnich 30 dniach i tylko na pulpicie albo stronie głównej; później samouczek otwiera pozycja
 * „Pierwsze kroki” w menu konta. Stan kroków liczymy z danych konta przy wejściu, zmianie strony, powrocie
 * do karty i co kilka sekund dla bieżącego kroku, dopóki samouczek jest włączony.
 */

const SamouczekContext = createContext(null)

const DNI_NOWEGO_KONTA = 30
const CO_ILE_MS = 7000
const TRASY_BEZ_PANELU = ['/logowanie', '/rejestracja', '/dolacz', '/potwierdz-email', '/reset-hasla', '/nowe-haslo']
const TRASY_POWITANIA = ['/', '/pulpit']
// Źródła danych bieżącego kroku (sprawdzane co kilka sekund); reszta przy zmianie strony.
const ZRODLA_KROKU = {
  firma: ['profil'], rynek: ['ankieta'], klienci: ['klienci'], zespol: ['zespol'], kalendarz: ['klienci'], cennik: ['cennik'], sprzet: ['preferencje'],
}
const WSZYSTKIE_ZRODLA = ['profil', 'ankieta', 'klienci', 'zespol', 'preferencje', 'cennik']

export const SamouczekProvider = ({ children }) => {
  const { isAuthenticated, profil, firmowe, odswiezProfil, moze } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const email = profil?.email || ''

  const [pamiec, setPamiec] = useState(PUSTA_PAMIEC)
  const [dane, setDane] = useState({})
  const [zaladowane, setZaladowane] = useState(false)
  const [powitanie, setPowitanie] = useState(false)
  const [odlozone, setOdlozone] = useState(false)
  const [podswietlenie, setPodswietlenie] = useState(null)
  const [komunikat, setKomunikat] = useState('')

  useEffect(() => {
    setPamiec(email ? czytajPamiec(email) : PUSTA_PAMIEC)
    setDane({})
    setZaladowane(false)
  }, [email])

  const ustawPamiec = useCallback((zmiana) => {
    setPamiec((stara) => {
      const nowa = typeof zmiana === 'function' ? zmiana(stara) : { ...stara, ...zmiana }
      if (email) zapiszPamiec(email, nowa)
      return nowa
    })
  }, [email])

  const firma = profil?.company || null
  const rola = firma?.role || null
  const bezTypu = Boolean(profil) && !firmowe && !firma && !(profil.user_types || []).length
  // Konto zablokowane ponad limitem planu (BE-04) nie ma klientów ani zespołu, więc samouczek nie ma czego pokazać.
  const dostepny = jestPolska && isAuthenticated && Boolean(profil) && (firmowe || bezTypu) && !firma?.zablokowane
  const noweKonto = Boolean(profil?.date_joined)
    && Date.now() - new Date(profil.date_joined).getTime() < DNI_NOWEGO_KONTA * 864e5
  const aktywny = dostepny && pamiec.stan === 'aktywny'

  const fakty = useMemo(() => ({
    firmowe,
    firma: Boolean(firma),
    rola,
    uprawnienia: firma?.uprawnienia || [],
    limit: firma?.limit_kont || 5,
    logo: Boolean(profil?.logo_wersja),
    ...dane,
  }), [firmowe, firma, rola, profil?.logo_wersja, dane])

  const stan = useMemo(() => stanSamouczka(fakty, pamiec), [fakty, pamiec])

  // Dane kroków. Najnowsze wartości w refie, żeby zegar i nasłuchy nie odtwarzały się przy każdej zmianie.
  const ref = useRef({})
  ref.current = { dostepny, firmowe, firma, rola, moze, odswiezProfil, profil }
  const sprawdz = useCallback(async (zrodla = WSZYSTKIE_ZRODLA) => {
    const r = ref.current
    if (!r.dostepny) return
    const wyniki = {}
    const zadania = []
    // Profil całej aplikacji odświeżamy tylko przy zmianie, którą widać w krokach (firma, logo, rodzaj konta):
    // każde odświeżenie to nowy obiekt profilu i ponowne rysowanie wszystkiego, co go używa.
    if (zrodla.includes('profil')) {
      zadania.push(getProfile().then((res) => {
        const n = res.ok ? res.data : null
        const s = r.profil
        if (n && (!s || n.account_type !== s.account_type || Boolean(n.company) !== Boolean(s.company)
          || n.logo_wersja !== s.logo_wersja || n.company?.role !== s.company?.role)) return r.odswiezProfil()
        return null
      }))
    }
    if (r.firmowe && zrodla.includes('klienci')) {
      zadania.push(listKlienci().then((res) => {
        if (!res.ok) return
        const klienci = klienciWlasciwi(res.data.klienci)
        wyniki.klientow = klienci.length
        wyniki.wlasneTerminy = maWlasneTerminy(klienci, terminyKlienta)
      }))
    }
    if (r.firma && (r.rola === 'owner' || r.moze('zespol')) && zrodla.includes('zespol')) {
      zadania.push(getZespol().then((res) => {
        if (!res.ok) return
        wyniki.osob = (res.data.osoby || []).length
        wyniki.zaproszen = (res.data.zaproszenia || []).filter((z) => z.stan === 'wazne').length
      }))
    }
    if (r.firma && zrodla.includes('ankieta')) {
      zadania.push(profilFirmy().then((res) => { if (res.ok) wyniki.ankieta = Boolean(res.data.uzupelniony && !res.data.domyslny) }))
    }
    if (zrodla.includes('preferencje')) {
      zadania.push(getKitPreferences().then((res) => { if (res.ok) wyniki.preferencje = preferencjeUstawione(res.data) }))
    }
    if (r.firmowe && zrodla.includes('cennik') && (!r.firma || r.rola === 'owner' || r.moze('wycena'))) {
      zadania.push(getQuoteSettings().then((res) => { if (res.ok) wyniki.cennikPodpis = podpisCennika(res.data) }))
    }
    await Promise.all(zadania)
    setDane((d) => ({ ...d, ...wyniki }))
    setZaladowane(true)
  }, [])

  // Pierwsze sprawdzenie, gdy samouczek może się pokazać albo jest włączony.
  const potrzebny = dostepny && (pamiec.stan === null || pamiec.stan === 'aktywny')
  useEffect(() => { if (potrzebny) sprawdz() }, [potrzebny, firmowe, Boolean(firma), sprawdz]) // eslint-disable-line react-hooks/exhaustive-deps

  // Zmiana strony: wszystko (z małym opóźnieniem, bo zapis na poprzedniej stronie mógł się jeszcze nie skończyć).
  const trasa = `${location.pathname}${location.search}`
  useEffect(() => {
    if (!aktywny) return undefined
    const t = setTimeout(() => sprawdz(), 400)
    return () => clearTimeout(t)
  }, [aktywny, trasa, sprawdz])

  // Bieżący krok co kilka sekund i po powrocie do karty.
  const biezacyKlucz = stan.biezacy?.krok.klucz || null
  useEffect(() => {
    if (!aktywny || !biezacyKlucz) return undefined
    const zrodla = ZRODLA_KROKU[biezacyKlucz] || WSZYSTKIE_ZRODLA
    const zegar = setInterval(() => { if (document.visibilityState === 'visible') sprawdz(zrodla) }, CO_ILE_MS)
    const powrot = () => { if (document.visibilityState === 'visible') sprawdz() }
    window.addEventListener('focus', powrot)
    document.addEventListener('visibilitychange', powrot)
    return () => {
      clearInterval(zegar)
      window.removeEventListener('focus', powrot)
      document.removeEventListener('visibilitychange', powrot)
    }
  }, [aktywny, biezacyKlucz, sprawdz])

  // Odwiedzony kalendarz odhacza krok „Kalendarz” (krok pokazuje, gdzie on jest; terminy dodaje się potem).
  useEffect(() => {
    if (!dostepny || pamiec.stan !== 'aktywny') return
    const kalendarz = location.pathname === '/klienci' && new URLSearchParams(location.search).get('widok') === 'kalendarz'
    if (kalendarz && (dane.klientow || 0) > 0 && !pamiec.odwiedzone.includes('kalendarz')) {
      ustawPamiec((p) => ({ ...p, odwiedzone: [...p.odwiedzone, 'kalendarz'] }))
    }
  }, [dostepny, pamiec.stan, pamiec.odwiedzone, location.pathname, location.search, dane.klientow, ustawPamiec])

  // Cennik w chwili, gdy krok „Twój cennik” staje się bieżący: zmiana względem niego odhacza krok.
  useEffect(() => {
    if (aktywny && biezacyKlucz === 'cennik' && dane.cennikPodpis && !pamiec.cennikStart) {
      ustawPamiec({ cennikStart: dane.cennikPodpis })
    }
  }, [aktywny, biezacyKlucz, dane.cennikPodpis, pamiec.cennikStart, ustawPamiec])

  // Bieżący krok właśnie zrobiony: rozwijamy panel z „Zrobione” i „Dalej” (po „Pokaż mi” był zwinięty).
  const stanBiezacego = stan.biezacy?.stan || null
  const poprzedni = useRef({ klucz: null, stan: null })
  useEffect(() => {
    const p = poprzedni.current
    if (aktywny && biezacyKlucz && p.klucz === biezacyKlucz && p.stan && p.stan !== 'zrobiony' && stanBiezacego === 'zrobiony') {
      ustawPamiec({ zwiniety: false })
    }
    poprzedni.current = { klucz: biezacyKlucz, stan: stanBiezacego }
  }, [aktywny, biezacyKlucz, stanBiezacego, ustawPamiec])

  // Krok „Twoja firma” ma dwie części: po podpięciu firmy od razu podświetlamy logo (ta sama zakładka).
  // `null` do pierwszego wczytania profilu: wczytanie konta z firmą to nie jest „właśnie podpięta firma”.
  const bylaFirma = useRef(null)
  useEffect(() => {
    if (!profil) { bylaFirma.current = null; return }
    const teraz = Boolean(firma)
    if (bylaFirma.current === false && teraz && aktywny && biezacyKlucz === 'firma' && !profil.logo_wersja) {
      const krok = krokPoKluczu('firma')
      const f = { ...fakty, firma: true }
      setPodswietlenie({ klucz: 'firma', cel: krok.cel(f), dymek: krok.dymek(f) })
    }
    bylaFirma.current = teraz
  }, [profil, firma, aktywny, biezacyKlucz, fakty])

  // Okno powitania: nowe konto, pierwszy raz, pulpit albo strona główna, gdy jest coś do zrobienia.
  useEffect(() => {
    if (!dostepny || !zaladowane || pamiec.stan !== null || odlozone || !noweKonto) return
    if (!TRASY_POWITANIA.includes(location.pathname)) return
    if (stan.koniec) return
    setPowitanie(true)
  }, [dostepny, zaladowane, pamiec.stan, odlozone, noweKonto, location.pathname, stan.koniec])

  useEffect(() => {
    if (!komunikat) return undefined
    const t = setTimeout(() => setKomunikat(''), 6000)
    return () => clearTimeout(t)
  }, [komunikat])

  // ── Akcje ────────────────────────────────────────────────────────────────
  const pokaz = useCallback((klucz) => {
    const krok = krokPoKluczu(klucz)
    if (!krok) return
    const adres = krok.adres(fakty)
    if (adres !== trasa) navigate(adres)
    // Pokazany krok zostaje bieżącym: po jego zrobieniu panel pokaże „Zrobione” i „Dalej”, zamiast przeskoczyć.
    // Panel zwija się do pigułki, żeby nie zasłaniał formularza, i rozwija sam, gdy krok będzie zrobiony.
    ustawPamiec((p) => ({ ...p, krok: klucz, zwiniety: true }))
    setPodswietlenie({ klucz, cel: krok.cel(fakty), dymek: krok.dymek(fakty) })
  }, [fakty, trasa, navigate, ustawPamiec])

  const rozpocznij = useCallback(({ odRazu = false } = {}) => {
    ustawPamiec({ stan: 'aktywny', zwiniety: false, krok: stan.biezacy?.krok.klucz || null })
    setPowitanie(false)
    if (odRazu) pokaz(stan.biezacy?.krok.klucz || 'firma')
  }, [ustawPamiec, pokaz, stan.biezacy])

  const pominCalosc = useCallback(() => {
    ustawPamiec({ stan: 'pominiety' })
    setPowitanie(false)
    setPodswietlenie(null)
    if (firmowe) setKomunikat('Samouczek znajdziesz w menu konta, w pozycji Pierwsze kroki.')
  }, [ustawPamiec, firmowe])

  const pozniej = useCallback(() => { setOdlozone(true); setPowitanie(false) }, [])

  const otworz = useCallback(() => {
    ustawPamiec((p) => ({ ...p, stan: 'aktywny', zwiniety: false }))
    sprawdz()
  }, [ustawPamiec, sprawdz])

  const wybierzKrok = useCallback((klucz) => {
    ustawPamiec((p) => ({ ...p, krok: klucz, zwiniety: false, pominiete: p.pominiete.filter((k) => k !== klucz) }))
  }, [ustawPamiec])

  const pominKrok = useCallback((klucz) => {
    setPodswietlenie(null)
    ustawPamiec((p) => {
      const pominiete = p.pominiete.includes(klucz) ? p.pominiete : [...p.pominiete, klucz]
      return { ...p, pominiete, krok: krokPo(klucz, fakty, { ...p, pominiete }) }
    })
  }, [ustawPamiec, fakty])

  const dalej = useCallback(() => {
    setPodswietlenie(null)
    ustawPamiec((p) => ({ ...p, krok: krokPo(p.krok || biezacyKlucz, fakty, p) }))
  }, [ustawPamiec, fakty, biezacyKlucz])

  const potwierdz = useCallback((klucz) => {
    setPodswietlenie(null)
    ustawPamiec((p) => (p.potwierdzone.includes(klucz) ? p : { ...p, potwierdzone: [...p.potwierdzone, klucz] }))
  }, [ustawPamiec])

  const zwin = useCallback((zwiniety) => ustawPamiec({ zwiniety }), [ustawPamiec])
  const zakoncz = useCallback(() => { setPodswietlenie(null); ustawPamiec({ stan: 'ukonczony' }) }, [ustawPamiec])
  const zamknijPodswietlenie = useCallback(() => setPodswietlenie(null), [])

  const naTrasieBezPanelu = TRASY_BEZ_PANELU.some((t) => location.pathname === t || location.pathname.startsWith(`${t}/`))

  const wartosc = useMemo(() => ({
    dostepny,
    bezTypu,
    aktywny,
    widocznyPanel: aktywny && !naTrasieBezPanelu,
    // Projekt 3D zajmuje cały ekran: tam tylko zwinięta pigułka.
    wymuszoneZwiniecie: location.pathname.startsWith('/projekt-3d'),
    stan,
    fakty,
    pamiec,
    powitanie,
    podswietlenie,
    komunikat,
    imie: profil?.first_name || '',
    nazwaFirmy: firma?.name || '',
    rozpocznij, pominCalosc, pozniej, otworz, wybierzKrok, pominKrok, dalej, potwierdz, pokaz, zwin, zakoncz,
    zamknijPodswietlenie,
    zamknijKomunikat: () => setKomunikat(''),
  }), [dostepny, bezTypu, aktywny, naTrasieBezPanelu, location.pathname, stan, fakty, pamiec, powitanie, podswietlenie,
    komunikat, profil?.first_name, firma?.name, rozpocznij, pominCalosc, pozniej, otworz, wybierzKrok, pominKrok, dalej,
    potwierdz, pokaz, zwin, zakoncz, zamknijPodswietlenie])

  return <SamouczekContext.Provider value={wartosc}>{children}</SamouczekContext.Provider>
}

// Bez dostawcy (np. w testach pojedynczych komponentów) samouczka po prostu nie ma.
const BRAK = { dostepny: false, aktywny: false, stan: { zrobione: 0, wszystkich: 0, koniec: false, kroki: [] }, otworz: () => {} }

export const useSamouczek = () => useContext(SamouczekContext) || BRAK
